import { useEffect, useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { tracking } from '@iwoca/frontend-tracking-library';
import { IconLockCloseFilled } from '@iwoca/icons';
import { GetCompanySearchResponse } from '@iwoca/lapi-client/edge';
import { Button, Modal } from '@iwoca/orion';
import { useInvalidateAuth } from 'hooks/useAuth';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { generateUKAddress } from 'utils/addressBuilder';
import { getAmplitudeDefaultEventProperties } from 'utils/amplitudeEvents';
import { PATH_NAMES } from 'utils/routes/pathNames';
import { TIntroducerType } from '../../registration.types';
import { AccountInformation } from './AccountInformation/AccountInformation';
import { CompanyInformation } from './CompanyInformation/CompanyInformation';
import {
  handleSaveDocuments,
  postIntroducerAccount,
  schema,
  handlePatchOnboardingFilesUploaded,
} from './registrationForm.helpers';
import styles from './RegistrationForm.module.css';
import { TRegistrationFormValues } from './registrationForm.types';
import { TermsAndConditions } from './TermsAndConditions/TermsAndConditions';

export const RegistrationForm = () => {
  const navigate = useNavigate();
  const invalidateAuth = useInvalidateAuth();
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [introducerType, setIntroducerType] = useState<TIntroducerType>();
  const [selectedCompany, setSelectedCompany] =
    useState<GetCompanySearchResponse['data'][number]>();
  const [isCompanySearch, setIsCompanySearch] = useState(true);

  const introducerTypeFromQueryParameter: TIntroducerType =
    searchParams.get('introducerType') === 'accountant'
      ? 'accountant'
      : 'broker';

  const methods = useForm<TRegistrationFormValues>({
    defaultValues: {
      introducerType: introducerTypeFromQueryParameter,
    },
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    if (introducerTypeFromQueryParameter) {
      setIntroducerType(introducerTypeFromQueryParameter);
    }
  }, [introducerTypeFromQueryParameter]);

  const onSubmit = async (values: TRegistrationFormValues) => {
    setIsLoading(true);

    tracking.ampli.ctaClick({
      'CTA text': 'Create an account',
      'CTA type': 'Submit',
      ...getAmplitudeDefaultEventProperties(),
    });

    const registeredTradingAddress = generateUKAddress(values);

    const response = await postIntroducerAccount({
      values: { ...values, registeredTradingAddress },
      onError: () => {
        setIsLoading(false);
        return setShowErrorModal(true);
      },
    });

    if (
      response.data.introducer_id &&
      values.certifiedIdFile &&
      values.bankStatementFile &&
      values.proofOfAddressFile
    ) {
      try {
        await handleSaveDocuments({
          selectedFiles: [
            values.certifiedIdFile,
            values.bankStatementFile,
            values.proofOfAddressFile,
          ],
          introducerId: response.data.introducer_id,
        });

        await handlePatchOnboardingFilesUploaded({
          introducerAccountId: response.data.introducer_account_id,
        });
      } catch {
        setIsLoading(false);
      }
      // Refresh auth before redirecting; otherwise ProductsLoader sees
      // the cached anonymous result and bounces the new user to /403.
      await invalidateAuth();
      navigate(PATH_NAMES.home());
    }

    setIsLoading(false);
  };

  return (
    <>
      <FormProvider {...methods}>
        <div className={styles.container}>
          <form
            className={styles.form}
            onSubmit={methods.handleSubmit(onSubmit)}
          >
            <CompanyInformation
              isCompanySearch={isCompanySearch}
              introducerType={introducerType}
              selectedCompany={selectedCompany}
              setSelectedCompany={setSelectedCompany}
              setIntroducerType={setIntroducerType}
              setIsCompanySearch={setIsCompanySearch}
            />
            <AccountInformation
              companyNumber={selectedCompany?.number}
              isCompanySearch={isCompanySearch}
            />
            <div className={styles.footer}>
              <TermsAndConditions />
              <p className={styles.submitDescription}>
                We'll process your registration.
              </p>
              <Button
                variant="primary"
                size="small"
                type="submit"
                loading={isLoading}
                className={styles.submitButton}
              >
                Create an account
                <IconLockCloseFilled />
              </Button>
            </div>
          </form>
        </div>
      </FormProvider>
      <Modal
        closeIcon
        isOpen={showErrorModal}
        onClose={() => setShowErrorModal(false)}
        title="There's a problem with the info you've entered."
        className={styles.modal}
      >
        <p className={styles.modalContent}>
          If you have an account with iwoca, use the login button in the top
          right, otherwise give us a call and we'll help you get logged in.
        </p>
      </Modal>
    </>
  );
};
